import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Stack, Typography } from '@mui/material';

import { AddButton } from 'components/Button';
import { Loader } from 'components/Loader';
import { DetailViewTabList } from 'components/TabList';

import { useQnaSummary } from '../api/getQnaSummary';
import { useMarkRead } from '../api/markRead';
import { formatDateTime } from '../utils/formatDate';
import { AskQuestionModal } from './AskQuestionModal';
import { DraftNotificationsList } from './DraftNotificationsList';
import { NotificationFormModal } from './NotificationFormModal';
import { PublishedList } from './PublishedList';
import { QnaEmpty } from './QnaCard';
import { SentList } from './SentList';
import { UnansweredList } from './UnansweredList';

export const qnaTabName = 'qna';

const buttonSx = ({ palette }) => ({
  height: 42,
  px: 1.5,
  border: `1px solid ${palette.grey[200]}`,
});

const subtabsFor = (summary) => {
  const { role, counts, unread } = summary;
  const published = {
    label: 'Published',
    value: 'published',
    count: counts.published,
    dot: unread.published,
  };

  if (role === 'answerer') {
    return [
      published,
      { label: 'Unanswered questions', value: 'unanswered', count: counts.unanswered },
      { label: 'Draft notifications', value: 'drafts', count: counts.draft_notifications },
    ];
  }
  if (role === 'supplier') {
    return [published, { label: 'Sent', value: 'sent', count: counts.sent, dot: unread.sent }];
  }
  return [published];
};

const DeadlineText = ({ deadline }) => {
  if (!deadline) return null;

  const passed = new Date(deadline) <= new Date();
  return (
    <Typography variant="body2" fontWeight={600} color={passed ? 'error.main' : 'text.primary'}>
      {passed
        ? `The deadline for questions passed on ${formatDateTime(deadline)}`
        : `Questions can be asked until ${formatDateTime(deadline)}`}
    </Typography>
  );
};

export const QnaTab = ({ currentTab, resource, id }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isVisible = currentTab === qnaTabName;
  const [modal, setModal] = useState(null);

  const { data: summary, isLoading, isError } = useQnaSummary(resource, id, { enabled: isVisible });
  const { mutate: markRead } = useMarkRead(resource, id);

  const subtabs = summary ? subtabsFor(summary) : [];
  const requestedSubtab = new URLSearchParams(location.search).get('subtab');
  const subtab = subtabs.some(({ value }) => value === requestedSubtab)
    ? requestedSubtab
    : 'published';
  const isAnswerer = summary?.role === 'answerer';
  const hasUnread = !isAnswerer && !!summary?.unread?.[subtab];

  useEffect(() => {
    if (isVisible && hasUnread) markRead(subtab);
  }, [isVisible, hasUnread, subtab, markRead]);

  if (!isVisible) return null;
  if (isLoading) return <Loader />;
  if (isError || !summary)
    return <QnaEmpty title="Q&A is not available" message="Please try again later" />;

  const switchSubtab = (_, value) =>
    navigate(`?tab=${qnaTabName}&subtab=${value}`, { replace: true });
  const closeModal = () => setModal(null);

  return (
    <Stack gap={2} flex={1}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        flexWrap="wrap"
      >
        <Stack flex={1} minWidth={280}>
          <DetailViewTabList value={subtab} onChange={switchSubtab} tabs={subtabs} multi />
        </Stack>
        <Stack direction="row" gap={1}>
          {summary.role === 'supplier' && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setModal('ask')}
              disabled={!summary.can_ask}
              sx={buttonSx}
            >
              Ask question
            </Button>
          )}
          {isAnswerer && (
            <AddButton color="secondary" onClick={() => setModal('notification')} sx={buttonSx}>
              Add notification
            </AddButton>
          )}
        </Stack>
      </Stack>

      <DeadlineText deadline={summary.questions_deadline} />

      {subtab === 'published' && (
        <PublishedList resource={resource} id={id} isAnswerer={isAnswerer} />
      )}
      {subtab === 'sent' && <SentList resource={resource} id={id} />}
      {subtab === 'unanswered' && <UnansweredList resource={resource} id={id} />}
      {subtab === 'drafts' && <DraftNotificationsList resource={resource} id={id} />}

      <AskQuestionModal
        resource={resource}
        id={id}
        open={modal === 'ask'}
        handleClose={closeModal}
      />
      <NotificationFormModal
        resource={resource}
        id={id}
        open={modal === 'notification'}
        handleClose={closeModal}
      />
    </Stack>
  );
};
